import React, { useMemo } from "react";
import { Joyride, STATUS } from "react-joyride";
import { useTour } from "@/lib/tour.jsx";
import { useAuth } from "@/lib/auth.jsx";

// Build role-aware step arrays. Each step targets an existing sidebar/topbar
// data-testid so we never need to navigate mid-tour to a page whose element
// might not be mounted.
function investorSteps() {
  return [
    {
      target: "body",
      placement: "center",
      disableBeacon: true,
      title: "Welcome to Propera",
      content:
        "Let's take a 60-second tour so you can invest with confidence. You can skip anytime.",
    },
    {
      target: '[data-testid="nav-home"]',
      title: "Your Home dashboard",
      content:
        "A quick pulse of your account: featured developments, verified developers, and shortcuts to everything else.",
    },
    {
      target: '[data-testid="nav-discover"]',
      title: "Discover properties",
      content:
        "Search and filter every verified development by country, tag, price and more. Great for finding your next investment.",
    },
    {
      target: '[data-testid="nav-portfolio"]',
      title: "Portfolio · Saved & Compare",
      content:
        "Save any development to your Watchlist, or add up to a few side-by-side in Compare to weigh yields, unit availability, and developer track record.",
    },
    {
      target: '[data-testid="nav-messages"]',
      title: "Messages · Express Interest",
      content:
        "Tap Express Interest on any development and we'll open a private thread with the developer here. Track status (New → Contacted → Closed) live.",
    },
    {
      target: '[data-testid="notification-bell"]',
      title: "Notifications",
      content:
        "Real-time alerts: new messages, status updates on your interests, price changes and more.",
    },
    {
      target: '[data-testid="profile-menu-trigger"]',
      placement: "bottom-end",
      title: "Profile & Settings",
      content:
        "Manage your profile, security, and preferences. You can replay this tour anytime from here — just tap 'Take App Tour'.",
    },
    {
      target: "body",
      placement: "center",
      title: "You're all set",
      content:
        "Head over to Discover to find your first opportunity. Welcome aboard — welcome home.",
    },
  ];
}

function developerSteps() {
  return [
    {
      target: "body",
      placement: "center",
      disableBeacon: true,
      title: "Welcome to Propera",
      content:
        "Quick tour of your Developer portal — how to list, manage leads, and chat with investors. Skip anytime.",
    },
    {
      target: '[data-testid="nav-dashboard"]',
      title: "Dashboard",
      content:
        "Your KPIs at a glance: total developments, active leads, conversations, and units sold.",
    },
    {
      target: '[data-testid="nav-properties"]',
      title: "Properties",
      content:
        "Create and manage every listing. The 5-step wizard walks you from basics all the way to verification documents.",
    },
    {
      target: '[data-testid="nav-leads"]',
      title: "Leads",
      content:
        "Every investor who expresses interest lands here. Filter by status, jump straight into the chat, and update their status.",
    },
    {
      target: '[data-testid="nav-dev-messages"]',
      title: "Messages",
      content:
        "Private threads for each interest. Update the status pill (New / Contacted / Closed) — investors see it in real time.",
    },
    {
      target: '[data-testid="notification-bell"]',
      title: "Notifications",
      content:
        "New investor interests, incoming messages and admin review outcomes appear here.",
    },
    {
      target: '[data-testid="profile-menu-trigger"]',
      placement: "bottom-end",
      title: "Profile & Settings",
      content:
        "Manage your developer profile, company details and security. Replay this tour anytime from 'Take App Tour' inside your profile.",
    },
    {
      target: "body",
      placement: "center",
      title: "You're ready",
      content:
        "List your first development from Properties → Add Development to start attracting verified investors.",
    },
  ];
}

export default function OnboardingTour() {
  const { user } = useAuth();
  const { run, finishTour } = useTour();

  const steps = useMemo(() => {
    if (!user) return [];
    return user.role === "developer" ? developerSteps() : investorSteps();
  }, [user]);

  function handleEvent(data /* , controls */) {
    const { type, status } = data || {};
    if (type === "tour:end") {
      finishTour({ skipped: status === STATUS.SKIPPED });
    }
  }

  if (!user || steps.length === 0) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      options={{ buttons: ["back", "close", "primary", "skip"] }}
      showProgress
      scrollToFirstStep
      spotlightPadding={6}
      onEvent={handleEvent}
      locale={{
        back: "Back",
        close: "Close",
        last: "Finish",
        next: "Next",
        skip: "Skip tour",
      }}
      styles={{
        options: {
          primaryColor: "#0E8C5A",
          textColor: "#0f172a",
          backgroundColor: "#ffffff",
          arrowColor: "#ffffff",
          overlayColor: "rgba(15, 23, 42, 0.55)",
          zIndex: 10000,
          width: 360,
        },
        tooltip: {
          borderRadius: 20,
          padding: 20,
          fontFamily: "inherit",
        },
        tooltipTitle: {
          fontSize: 16,
          fontWeight: 700,
          marginBottom: 6,
        },
        tooltipContent: {
          fontSize: 13.5,
          lineHeight: 1.55,
          color: "#475569",
          textAlign: "left",
          padding: 0,
        },
        buttonPrimary: {
          backgroundColor: "#0E8C5A",
          color: "#ffffff",
          borderRadius: 999,
          padding: "8px 18px",
          fontWeight: 600,
          fontSize: 13,
        },
        buttonBack: {
          color: "#334155",
          fontWeight: 500,
          fontSize: 13,
          marginRight: 8,
        },
        buttonSkip: {
          color: "#94a3b8",
          fontWeight: 500,
          fontSize: 12,
        },
        spotlight: {
          borderRadius: 16,
        },
      }}
      floatingOptions={{}}
    />
  );
}

// A small button that can be dropped anywhere (used in Profile) to
// let users replay the tour on demand.
export function TakeAppTourButton({ className = "", label = "Take App Tour" }) {
  const { startTour } = useTour();
  return (
    <button
      type="button"
      data-testid="take-app-tour-btn"
      onClick={startTour}
      className={
        className ||
        "inline-flex items-center gap-2 bg-propera hover:bg-propera-hover text-white rounded-full px-5 py-2.5 font-semibold text-sm transition"
      }
    >
      {label}
    </button>
  );
}
